import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { theme, Button, Space, App, Spin, Typography, Card, Avatar, Tag, Table, Tooltip } from 'antd';
import { ArrowLeftOutlined, UserOutlined, ReloadOutlined, WalletOutlined, WarningOutlined } from '@ant-design/icons';
import { CryptoWallet, UserDetailResponse } from '@mygate/common';
import { usersService } from '@/services/users';
import { formatDateTime } from '@/utils/date';

const { Text } = Typography;

export function WalletBalances() {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userDetail, setUserDetail] = useState<UserDetailResponse | null>(null);
  const [wallets, setWallets] = useState<CryptoWallet[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
      fetchWalletBalances();
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    if (!userId) return;

    try {
      const response = await usersService.getUserDetails(userId);
      if (response.data) {
        setUserDetail(response.data);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const fetchWalletBalances = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await usersService.getUserWallets(userId);
      if (response.success && response.data) {
        setWallets(response.data);
        setLastUpdated(new Date());
      }
    } catch (error) {
      message.error('Failed to fetch wallet balances');
      console.error('Error fetching wallet balances:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatEurValue = (value: number | undefined) => {
    if (value === undefined) return '-';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const columns = [
    {
      title: 'Asset',
      dataIndex: ['asset', 'symbol'],
      key: 'symbol',
      width: 100,
      render: (symbol: string) => <Text strong>{symbol}</Text>,
    },
    {
      title: 'Network',
      dataIndex: ['asset', 'network'],
      key: 'network',
      width: 120,
      render: (network: string) => <Tag>{network || 'native'}</Tag>,
    },
    {
      title: 'Balance',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: string, record: CryptoWallet['assets'][0]) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontFamily: 'monospace' }}>{amount || '0'}</Text>
          {record.hasUnconfirmed && (
            <Tooltip title={`Confirmed: ${record.confirmedAmount}, Unconfirmed: ${record.unconfirmedAmount}`}>
              <Tag color="orange" icon={<WarningOutlined />}>
                Pending
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Price (EUR)',
      dataIndex: 'priceEur',
      key: 'priceEur',
      width: 120,
      align: 'right' as const,
      render: (price: number | undefined) => (price != null ? formatEurValue(price) : '—'),
    },
    {
      title: 'Value (EUR)',
      dataIndex: 'valueEur',
      key: 'valueEur',
      width: 140,
      align: 'right' as const,
      render: (value: number | undefined) => (
        <Text strong style={{ color: value ? '#52c41a' : undefined }}>
          {formatEurValue(value)}
        </Text>
      ),
    },
  ];

  const totalPortfolioValue = wallets.reduce((total, wallet) => total + (wallet.totalValueEur || 0), 0);

  return (
    <>
      <div className="page-toolbar">
        <Space>
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>
            Kunden
          </Button>
          <Button type="text" onClick={() => window.close()}>
            Fenster schließen
          </Button>
        </Space>

        {userDetail && (
          <Space align="center">
            <Avatar size="large" icon={<UserOutlined />} style={{ backgroundColor: token.colorPrimary }} />
            <div style={{ lineHeight: 1.5 }}>
              <Text strong style={{ fontSize: '14px' }}>
                {userDetail.user.firstName} {userDetail.user.lastName}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {userDetail.user.phoneNumber} | {userDetail.user.email || 'No email'}
              </Text>
            </div>
          </Space>
        )}
      </div>

      <>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Space>
            <Button type="primary" icon={<ReloadOutlined />} onClick={fetchWalletBalances} loading={loading}>
              Refresh
            </Button>
            {lastUpdated && <Text type="secondary">Last updated: {formatDateTime(lastUpdated.toISOString())}</Text>}
          </Space>
          {totalPortfolioValue > 0 && (
            <Tag color="green" style={{ fontSize: '16px', padding: '4px 12px' }}>
              Total Portfolio: {formatEurValue(totalPortfolioValue)}
            </Tag>
          )}
        </div>

        {loading && wallets.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Fetching wallet balances...</Text>
              </div>
            </div>
          </Card>
        ) : wallets.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <WalletOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">No wallets found for this user</Text>
              </div>
            </div>
          </Card>
        ) : (
          wallets.map(wallet => (
            <Card
              key={wallet.id}
              title={
                <Space>
                  <WalletOutlined />
                  <Text strong>{wallet.id}</Text>
                </Space>
              }
              extra={
                wallet.totalValueEur !== undefined && (
                  <Tag color="green" style={{ fontSize: '14px' }}>
                    Total: {formatEurValue(wallet.totalValueEur)}
                  </Tag>
                )
              }
              style={{ marginBottom: 16 }}
            >
              <Table
                scroll={{ x: 620 }}
                dataSource={wallet.assets}
                columns={columns}
                rowKey={record => `${wallet.id}-${record.asset.symbol}-${record.asset.network || 'native'}`}
                pagination={false}
                size="small"
                loading={loading}
              />
            </Card>
          ))
        )}
      </>
    </>
  );
}
